import { safeDate, Reward, Task, Family, ChecklistItem, Tool, User, Room } from 'shared/types';
import { RewardDto, TaskDto, FamilyDto, ChecklistItemDto, ToolDto, UserDto, RoomDto } from 'shared/types/dto';

export const normalizeReward = (dto: RewardDto): Reward => {
  return {
    id: dto.id,
    title: dto.title,
    description: dto.description || '',
    points_required: dto.points_required,
    target_type: dto.target_type,
    target_id: dto.target_id || undefined,
    is_claimed: dto.is_claimed,
    claimed_by: dto.claimed_by || undefined,
    claimed_at: safeDate(dto.claimed_at || undefined),
    created_at: safeDate(dto.created_at)!,
    header_image: dto.header_image || undefined,
    progress_start_at: dto.progress_start_at ?? undefined,
    eligible_points: dto.eligible_points != null ? Number(dto.eligible_points) : undefined,
    // Must survive normalization, or every claimed reward looks "still to hand over" forever.
    handed_over_at: dto.handed_over_at ?? null,
    handed_over_by: dto.handed_over_by ?? null,
  };
};

const normalizeChecklistItem = (dto: ChecklistItemDto): ChecklistItem => ({
  id: dto.id,
  text: dto.text,
  images: dto.images || [],
  tool_ids: Array.isArray(dto.tool_ids) ? dto.tool_ids : [],
  timer_seconds: typeof dto.timer_seconds === 'number' && dto.timer_seconds > 0 ? dto.timer_seconds : null,
});

const normalizeTool = (dto: ToolDto): Tool => ({
  id: dto.id,
  name: dto.name,
  image: dto.image || undefined,
  created_at: safeDate(dto.created_at)!,
  tool_key: (dto as any).tool_key || undefined,
  tool_type: (dto as any).tool_type || undefined,
  is_consumable: (dto as any).is_consumable ?? undefined,
  image_url: (dto as any).image_url || undefined,
  // Translations from the library and the family's own edits (2026-10-13: these were dropped,
  // so tools showed their raw key instead of e.g. "Robotstøvsuger").
  description: (dto as any).description ?? null,
  name_i18n: (dto as any).name_i18n ?? null,
  description_i18n: (dto as any).description_i18n ?? null,
  customized: (dto as any).customized ?? undefined,
});

export const normalizeTask = (dto: TaskDto): Task => {
  return {
    id: dto.id,
    title: dto.title,
    description: dto.description,
    room_id: dto.room_id || null,
    points: dto.points,
    checklist: (dto.checklist || []).map(normalizeChecklistItem),
    tools: (dto.tools || []).map(tool => tool.id),
    checklist_state: dto.checklist_state || dto.checklistState || undefined,
    is_completed: dto.is_completed,
    verified: dto.verified,
    verified_by: dto.verified_by || undefined,
    verified_at: safeDate(dto.verified_at || undefined),
    verification_seen_at: safeDate(dto.verification_seen_at || undefined),
    assigned_to: dto.assigned_to || undefined,
    completed_by: dto.completed_by || undefined,
    completion_message: dto.completion_message || undefined,
    created_at: safeDate(dto.created_at)!,
    completed_at: safeDate(dto.completed_at || undefined),
    due_date: safeDate(dto.due_date || undefined),
    recurrence_days: dto.recurrence_days || undefined,
    // Repeat settings + age group (2026-10-13: these were dropped, so the task editor showed
    // "Recreate on validation" unticked for a task that had it on — and saving could switch it off).
    recreate_on_validation: dto.recreate_on_validation ?? false,
    days_to_complete: dto.days_to_complete ?? undefined,
    age_group: (dto.age_group as any) ?? undefined,
    series_id: dto.series_id ?? null,
    auto_available: dto.auto_available ?? false,
    is_available: dto.is_available ?? true,
    header_image: dto.header_image || undefined,
    header_image_url: dto.header_image_url ?? undefined,
    points_awarded_on_verification: dto.points_awarded_on_verification ?? undefined,
    misattributed_payout_repair_at: dto.misattributed_payout_repair_at ?? undefined,
    available_from: dto.available_from ?? null,
    // Chains (2026-10-13)
    next_series_id: dto.next_series_id ?? null,
    next_wait_hours: dto.next_wait_hours ?? null,
    is_waiting: dto.is_waiting ?? false,
    // Shared tasks (2026-10-14)
    share_group_id: dto.share_group_id ?? null,
    pending_update: dto.pending_update ?? null,
    repeat_schedule: dto.repeat_schedule ?? null,
    counts_for_pocket_money: dto.counts_for_pocket_money !== false,
    approval_note: dto.approval_note ?? null,
    not_done_reason: dto.not_done_reason ?? null,
  };
};

const normalizeUser = (dto: UserDto): User => ({
  id: dto.id,
  name: dto.name,
  email: dto.email || undefined,
  is_admin: dto.is_admin,
  member_type: dto.member_type,
  age_group: (dto as any).age_group ?? null,
  points: dto.points,
  avatar_url: (dto as any).avatar_url || undefined,
  coins: (dto as any).coins ?? (dto.stars ?? 0),
  stars: dto.stars || undefined,
  has_google_identity: !!(dto as any).has_google_identity,
  password: '',
});

const normalizeRoom = (dto: RoomDto): Room => ({
  id: dto.id,
  name: dto.name,
  original_name: dto.original_name,
  image_url: dto.image_url ?? null,
});

export const normalizeFamily = (dto: FamilyDto): Family => {
  return {
    id: dto.id,
    name: dto.name,
    admin_id: dto.admin_id,
    onboarding_completed_at: dto.onboarding_completed_at ?? undefined,
    members: (dto.members || []).map(normalizeUser),
    rooms: (dto.rooms || []).map(normalizeRoom),
    tasks: (dto.tasks || []).map(normalizeTask),
    rewards: (dto.rewards || []).map(normalizeReward),
    tools: (dto.tools || []).map(normalizeTool),
    completed_total: dto.completed_total ?? null,
    completed_has_more: !!dto.completed_has_more,
    stats_visibility: dto.stats_visibility ?? 'team',
    adults_need_approval: dto.adults_need_approval === true,
    deleted_members: (dto.deleted_members || []).map((m) => ({ ...m })),
    billing: dto.billing ?? null,
    pocket_money_enabled: !!(dto as any).pocket_money_enabled,
  };
};


